import { useIngredientTagStore } from "./ingredient-tag-store";

import { defineStore } from "pinia";
import { isEqual, cloneDeep } from "lodash-es";

import {
  ingredientMetaFilterUserPath,
  ingredientMetaPath,
  translateToFrenchIngredientMetaPath,
} from "routes";

import { fetchScript } from "recipal-app/controllers/shared/requests";

const defaults = {
  userId: null,
  loaded: false,

  search: {
    q: "",
    archived: false,
    sortBy: undefined,
    sortDirection: "asc",
    tags: [],
    totalEntries: 0,
    currentPage: 1,
    perPage: 50,
  },

  ingredientMetas: [],

  initialSettings: null,
};

/* State keys which have an effect on the returned results mapped to their URLSearchParam key */
const queryParamMap = {
  q: "q",
  archived: "archived",
  sortBy: "sort_by",
  sortDirection: "sort_direction",
  tags: "tags[]",
  perPage: "per_page",
  currentPage: "page",
};
const advancedFilterKeys = ["tags", "archived"];

export const useIngredientMetaStore = (storeName = "ingredientMeta") => {
  return defineStore(storeName, {
    state: () => {
      return {
        ...cloneDeep(defaults),
        initialSettings: {},
      };
    },

    getters: {
      queryParamMap: (state) => {
        return queryParamMap;
      },

      /* Returns the keys that the user has configured that affect filters */
      configuredFilterKeys: (state) => {
        const pristine = { ...defaults.search, ...(state.initialSettings?.search ?? {}) };

        const diff = Object.keys(queryParamMap)
          .filter((k) => !isEqual(state.search[k], pristine[k]))
          .map((k) => [k, [state.search[k], pristine[k]]]);

        return Object.fromEntries(diff);
      },

      hasQuery: (state) => {
        return state.search.q !== "";
      },

      hasAdvancedFilters: (state) => {
        return Object.keys(state.configuredFilterKeys).some((k) => advancedFilterKeys.includes(k));
      },

      requestQueryParams: (state) => {
        const p = new URLSearchParams();

        if (state.search.q !== "") {
          p.append(queryParamMap["q"], state.search.q);
        }

        p.append(queryParamMap["archived"], state.search.archived);

        if (state.search.sortBy) {
          p.append(queryParamMap["sortBy"], state.search.sortBy);
        }
        p.append(queryParamMap["sortDirection"], state.search.sortDirection);

        state.search.tags.forEach((t) => {
          p.append(queryParamMap["tags"], t.name);
        });

        p.append(queryParamMap["perPage"], state.search.perPage);
        p.append(queryParamMap["currentPage"], state.search.currentPage);

        return p;
      },

      requestQueryString() {
        return this.requestQueryParams.toString();
      },
    },

    actions: {
      assignInitial(values = {}, { patch = false } = {}) {
        this.initialSettings = { ...values };

        if (patch) {
          this.$patch(values);
        }
      },

      loadInitial() {
        if (!this.loaded) {
          this.loadIngredientMetaPage();
        }
      },

      setSort(sortBy, sortDirection) {
        this.search.sortBy = sortBy;
        if (sortDirection !== undefined) {
          this.search.sortDirection = sortDirection;
        }

        this.loadIngredientMetaPage(1);
      },

      toggleSort(field) {
        if (this.search.sortBy === field) {
          this.setSort(field, this.search.sortDirection === "desc" ? "asc" : "desc");
        } else {
          this.setSort(field, "asc");
        }
      },

      async updateIngredientMeta(ingredientMeta, key, value, recipeId) {
        try {
          await fetchScript(ingredientMetaPath(ingredientMeta.id), {
            method: "PUT",
            body: JSON.stringify({ ingredient_meta: { [key]: value }, recipe_id: recipeId }),
          });

          this.ingredientMetas.find((im) => im.id == ingredientMeta.id)[key] = value;

          return true;
        } catch (error) {
          return false;
        }
      },

      async translateIngredientMeta(ingredientMeta) {
        try {
          const response = await fetch(translateToFrenchIngredientMetaPath(ingredientMeta.id), {
            method: "GET",
            headers: {
              Accept: "text/plain",
            },
          });

          if (!response.ok) {
            return false;
          }

          return await response.text();
        } catch (error) {
          return false;
        }
      },

      async loadIngredientMetaPage(newPage) {
        if (newPage !== undefined) {
          this.search.currentPage = newPage;
        }

        const response = await fetch(
          `${ingredientMetaFilterUserPath(this.userId)}?${this.requestQueryString}`,
          {
            method: "POST",
            headers: {
              "Accept": "application/json",
              "Content-Type": "application/json",
            },
            body: null,
          }
        );

        const json = await response.json();

        this.ingredientMetas = json.ingredient_metas;
        this.search.totalEntries = json.total_entries;
        this.search.perPage = json.per_page;
        this.search.currentPage = json.current_page;
        this.loaded = true;
      },

      setQueryFilter(q) {
        if (this.search.q == q) {
          return;
        }

        this.search.q = q;
        this.loadIngredientMetaPage(1);
      },

      setSearchFilter(key, value) {
        if (this.search[key] !== value) {
          this.search[key] = value;
          this.loadIngredientMetaPage(1);
        }
      },

      addTagFilter(tag) {
        if (this.search.tags.some((t) => t.name == tag.name)) {
          // tag already exists in filter
          return;
        }

        this.setTagFilter([...this.search.tags, tag]);
      },

      setTagFilter(tags) {
        this.search.tags = tags;
        this.loadIngredientMetaPage(1);
      },

      // Sets the search from URLSearchParams. The opposite of requestQueryParams getter
      async setFromSearchParams(searchParams) {
        const newState = {};
        const tagStore = useIngredientTagStore();

        const defaultState = (key) => this.initialSettings?.search?.key ?? defaults[key];

        if (searchParams.has(queryParamMap["q"])) {
          newState["q"] = searchParams.get(queryParamMap["q"]);
        }

        if (searchParams.has(queryParamMap["perPage"])) {
          newState["perPage"] =
            parseInt(searchParams.get(queryParamMap["perPage"])) || defaultState("perPage");
        }

        if (searchParams.has(queryParamMap["currentPage"])) {
          newState["currentPage"] =
            parseInt(searchParams.get(queryParamMap["currentPage"])) || defaultState("currentPage");
        }

        if (searchParams.has(queryParamMap["archived"])) {
          newState["archived"] = searchParams.get(queryParamMap["archived"]);
        }

        if (searchParams.has(queryParamMap["sortBy"])) {
          const sort =
            searchParams.get(queryParamMap["sortBy"]) == "name" ? "name" : defaultState("sortBy");
          newState["sortBy"] = sort;
        }

        if (searchParams.has(queryParamMap["sortDirection"])) {
          newState["sortDirection"] =
            searchParams.get(queryParamMap["sortDirection"]) == "asc" ? "asc" : "desc";
        }

        if (searchParams.has(queryParamMap["tags"])) {
          const tagNames = searchParams.getAll(queryParamMap["tags"]);

          if (!tagStore.loaded) {
            tagStore.setUserId(this.userId);
            await tagStore.loadAllTags();
          }

          newState["tags"] = tagNames.map((name) => tagStore.getByName(name)).filter((tag) => tag);
        }

        this.resetSearch({ search: newState });
      },

      resetSearch(overrides = {}) {
        const { userId, initialSettings } = { ...this };
        this.$patch({
          ...defaults,
          ...initialSettings,
          userId: userId,
          initialSettings: initialSettings,
          ...overrides,
        });

        this.loadIngredientMetaPage(this.currentPage);
      },

      // Clears filter state (q, tags, archived) but preserves sort/perPage so
      // users don't lose their table ordering when clearing filters.
      resetFilters() {
        this.$patch({
          search: {
            ...this.search,
            q: "",
            tags: [],
            archived: false,
            currentPage: 1,
          },
        });

        this.loadIngredientMetaPage(1);
      },
    },
  })();
};
